import React from 'react';
import type { AgroForecastDay, AgroForecastHour } from '../../../domain/Weather';
import { weatherCodeToIcon, weatherCodeToLabel } from '../../../domain/Weather';
import { compassPoint } from '../../../domain/WeatherStation';
import WeatherIcon from '../WeatherIcon';

const dayLabel = (isoDate: string, index: number) => {
  if (index === 0) return 'Hoy';
  if (index === 1) return 'Mañana';
  const [y, m, d] = isoDate.split('-').map(Number);
  const label = new Date(y, m - 1, d).toLocaleDateString('es-AR', { weekday: 'short' }).replace('.', '');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// Flechita que apunta hacia donde sopla el viento (dirección + 180°).
export const WindArrow: React.FC<{ direction: number; size?: number }> = ({ direction, size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ transform: `rotate(${direction + 180}deg)` }} aria-hidden="true">
    <path d="M12 3 L18 15 L12 12 L6 15 Z" fill="currentColor" />
  </svg>
);

// Pronóstico diario: la barra de cada día va de su mínima a su máxima sobre
// la escala de toda la semana, así se comparan los días entre sí.
export const ForecastDays: React.FC<{ days: AgroForecastDay[]; focus?: 'temperature' | 'rain' | 'wind' | 'humidity' }> = ({
  days,
  focus = 'temperature',
}) => {
  const weekMin = Math.min(...days.map((d) => d.temperatureMin));
  const weekMax = Math.max(...days.map((d) => d.temperatureMax));
  const span = weekMax - weekMin || 1;

  return (
    <div className="forecast-days">
      {days.map((day, i) => (
        <div className="forecast-day" key={day.date}>
          <span className="forecast-day-name">{dayLabel(day.date, i)}</span>
          <span className="forecast-day-icon" title={weatherCodeToLabel(day.weatherCode)}>
            <WeatherIcon icon={weatherCodeToIcon(day.weatherCode)} size={20} />
          </span>
          <span className={`forecast-day-rain ${day.precipitationSum >= 0.5 ? 'wet' : ''}`}>
            {day.precipitationSum >= 0.1 ? `${Math.round(day.precipitationSum * 10) / 10} mm` : '—'}
            <small>{Math.round(day.precipitationProbability)}%</small>
          </span>
          {focus === 'wind' ? (
            <span className="forecast-day-wind">
              <WindArrow direction={day.windDirectionDominant} />
              {Math.round(day.windSpeedMax)} km/h
              <small>ráf. {Math.round(day.windGustsMax)} · {compassPoint(day.windDirectionDominant)}</small>
            </span>
          ) : (
            <span className="forecast-day-temps">
              <span className="forecast-day-min">{Math.round(day.temperatureMin)}°</span>
              <span className="forecast-day-bar">
                <span
                  style={{
                    left: `${((day.temperatureMin - weekMin) / span) * 100}%`,
                    right: `${100 - ((day.temperatureMax - weekMin) / span) * 100}%`,
                  }}
                />
              </span>
              <span className="forecast-day-max">{Math.round(day.temperatureMax)}°</span>
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const hourLabel = (iso: string, i: number) => (i === 0 ? 'Ahora' : `${iso.slice(11, 13)}h`);

// Próximas horas, en una tira horizontal. `metric` elige qué dato destacar.
export const ForecastHours: React.FC<{ hours: AgroForecastHour[]; metric: 'temperature' | 'humidity' | 'rain' | 'wind'; count?: number }> = ({
  hours,
  metric,
  count = 24,
}) => (
  <div className="forecast-hours">
    {hours.slice(0, count).map((h, i) => (
      <div className="forecast-hour" key={h.time}>
        <span className="forecast-hour-time">{hourLabel(h.time, i)}</span>
        {metric === 'wind' ? (
          <>
            <span className="forecast-hour-arrow">
              <WindArrow direction={h.windDirection} size={16} />
            </span>
            <span className="forecast-hour-value">{Math.round(h.windSpeed)}</span>
            <span className="forecast-hour-sub">ráf. {Math.round(h.windGusts)}</span>
          </>
        ) : metric === 'rain' ? (
          <>
            <WeatherIcon icon={weatherCodeToIcon(h.weatherCode)} size={18} />
            <span className="forecast-hour-value">{h.precipitation >= 0.1 ? Math.round(h.precipitation * 10) / 10 : '0'}</span>
            <span className="forecast-hour-sub">{Math.round(h.precipitationProbability)}%</span>
          </>
        ) : metric === 'humidity' ? (
          <>
            <span className="forecast-hour-meter">
              <span style={{ height: `${h.humidity}%` }} />
            </span>
            <span className="forecast-hour-value">{Math.round(h.humidity)}%</span>
          </>
        ) : (
          <>
            <WeatherIcon icon={weatherCodeToIcon(h.weatherCode)} size={18} />
            <span className="forecast-hour-value">{Math.round(h.temperature)}°</span>
            <span className="forecast-hour-sub">{Math.round(h.humidity)}%</span>
          </>
        )}
      </div>
    ))}
  </div>
);
